// NavigationMenu.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import "../css/MenuItem.css";
import apiUrl from "../utility/apiUrl";

const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// The menu's slug field encodes location inconsistently across categories — MBA/
// Engineering spell it "...colleges-in-{city}" but Medical spells the exact same
// thing "...colleges-{city}" (no "in"), so parsing the slug alone silently misses
// every Medical location link. The human-written label is consistent everywhere
// ("... Colleges in {Place}"), so extract location from there instead and carry
// it as an explicit query param the destination page can read unambiguously.
const extractLocationFromLabel = (label) => {
  if (typeof label !== "string") return null;
  const match = label.match(/\bin\s+(.+)$/i);
  if (!match) return null;
  const place = match[1].trim();
  if (!place || /^india$/i.test(place)) return null;
  return place;
};

const buildCollegeListPath = (courseLabel, leafLabel, providedSlug) => {
  let base;
  if (providedSlug && typeof providedSlug === "string" && providedSlug.trim()) {
    base = providedSlug.startsWith("/") ? providedSlug : `/${providedSlug}`;
  } else {
    const course = slugify(courseLabel);
    const menuName = slugify(leafLabel);
    if (!course || !menuName) return null;
    base = `/college/${course}/top/${menuName}`;
  }

  const params = new URLSearchParams();
  const place = extractLocationFromLabel(leafLabel);
  if (place) params.set("city", place);
  // Matches the filter sidebar's own bucket label — the backend expands "Private"/
  // "Government" into every raw spelling the legacy data uses (Pvt/Private, Govt/
  // Public-Government) rather than requiring an exact match on one literal spelling.
  if (/\bprivate\b/i.test(leafLabel || "")) params.set("collegeType", "Private");

  const query = params.toString();
  return query ? `${base}?${query}` : base;
};

// Shared by desktop (MenuItem) and mobile (MobileNavigationMenu) so tapping a
// top-level category (MBA/Engineering/Medical) always has a sensible "Explore
// Colleges" landing page, picking the group's "... Colleges in India"-style
// leaf when present, else just the first leaf of the first group.
const findDefaultLeaf = (items) => {
  if (!Array.isArray(items)) return null;
  for (const group of items) {
    const leaves = group?.submenu;
    if (!Array.isArray(leaves)) continue;
    const best = leaves.find(
      (leaf) =>
        typeof leaf?.label === "string" &&
        /college/i.test(leaf.label) &&
        /india\b/i.test(leaf.label)
    );
    if (best) return best;
    if (leaves[0]) return leaves[0];
  }
  return null;
};

const MenuItem = ({ label, submenu }) => {
  const [isSubMenuVisible, setIsSubMenuVisible] = useState(false);
  const [openInnerIndex, setOpenInnerIndex] = useState(null);
  const [supportsHover, setSupportsHover] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
    return Boolean(window.matchMedia("(hover: hover) and (pointer: fine)").matches);
  });
  const navigate = useNavigate();
  const rootRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setSupportsHover(Boolean(mq.matches));
    update();

    if (typeof mq.addEventListener === "function") mq.addEventListener("change", update);
    else if (typeof mq.addListener === "function") mq.addListener(update);

    return () => {
      if (typeof mq.removeEventListener === "function") mq.removeEventListener("change", update);
      else if (typeof mq.removeListener === "function") mq.removeListener(update);
    };
  }, []);

  useEffect(() => {
    if (supportsHover) return;
    if (!isSubMenuVisible) return;

    const handlePointerDown = (event) => {
      const root = rootRef.current;
      const target = event?.target;
      if (!root || !target) return;
      if (root.contains(target)) return;

      setIsSubMenuVisible(false);
      setOpenInnerIndex(null);
    };

    document.addEventListener("pointerdown", handlePointerDown, { capture: true });
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, { capture: true });
    };
  }, [supportsHover, isSubMenuVisible]);

  const handleMouseEnter = () => {
    setIsSubMenuVisible(true);
  };

  const handleMouseLeave = () => {
    setIsSubMenuVisible(false);
    setOpenInnerIndex(null);
  };

  const handleTopLabelClick = () => {
    if (!Array.isArray(submenu) || submenu.length === 0) return;

    if (!supportsHover) {
      setIsSubMenuVisible((prev) => !prev);
      setOpenInnerIndex(null);
      return;
    }

    const defaultLeaf = findDefaultLeaf(submenu);
    const targetPath = defaultLeaf
      ? buildCollegeListPath(label, defaultLeaf.label, defaultLeaf.slug)
      : null;

    if (targetPath) {
      navigate(targetPath);
      return;
    }

    setIsSubMenuVisible((prev) => !prev);
  };

  const toggleInner = (index) => {
    setOpenInnerIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div
      ref={rootRef}
      className={`menu-item ${isSubMenuVisible ? "submenu-open" : ""}`}
      onMouseEnter={supportsHover ? handleMouseEnter : undefined}
      onMouseLeave={supportsHover ? handleMouseLeave : undefined}
    >
      <button type="button" className="menu-label" onClick={handleTopLabelClick}>
        {label}
        {submenu && <span className="arrow">&#9660;</span>}
      </button>
      <div className="submenu-container">
        <AnimatePresence>
          {isSubMenuVisible && (
            <motion.ul
              className="submenu"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              style={{ display: "block" }}
            >
            {submenu.map((item, index) => {
              const hasInner = Array.isArray(item?.submenu) && item.submenu.length > 0;
              const isInnerOpen = openInnerIndex === index;

              const subcategoryDefaultLeaf = hasInner
                ? item.submenu.find(
                    (leaf) =>
                      typeof leaf?.label === "string" &&
                      /college/i.test(leaf.label) &&
                      /india\b/i.test(leaf.label)
                  ) || item.submenu[0]
                : null;

              const subcategoryTargetPath = subcategoryDefaultLeaf
                ? buildCollegeListPath(label, subcategoryDefaultLeaf.label, subcategoryDefaultLeaf.slug)
                : null;

              return (
                <li
                  className={`submenu-item ${isInnerOpen ? "inner-open" : ""}`}
                  key={index}
                >
                  <button
                    type="button"
                    className="submenu-label"
                    onClick={() => {
                      if (hasInner) {
                        if (!supportsHover) {
                          toggleInner(index);
                          return;
                        }
                        if (subcategoryTargetPath) {
                          navigate(subcategoryTargetPath);
                          return;
                        }
                        toggleInner(index);
                        return;
                      }

                      if (subcategoryTargetPath) navigate(subcategoryTargetPath);
                    }}
                  >
                    {item.label}
                  </button>

                  {hasInner && (
                    <div className="inner-submenu">
                      <table border="0">
                        <tbody>
                          <tr>
                            <td className="column">
                              <ul className="inner-submenu-list">
                                {item.submenu
                                  .slice(0, 12)
                                  .map((innerItem, innerIndex) => {
                                    const to = buildCollegeListPath(
                                      label,
                                      innerItem.label,
                                      innerItem.slug
                                    );
                                    return (
                                      <li className="inner-menu-item" key={innerIndex}>
                                        {to ? (
                                          <Link
                                            to={to}
                                            onClick={() => {
                                              setIsSubMenuVisible(false);
                                              setOpenInnerIndex(null);
                                            }}
                                          >
                                            {innerItem.label}
                                          </Link>
                                        ) : (
                                          <span>{innerItem.label}</span>
                                        )}
                                      </li>
                                    );
                                  })}
                              </ul>
                            </td>
                            <td className="column">
                              <ul className="inner-submenu-list">
                                {item.submenu
                                  .slice(12, 24)
                                  .map((innerItem, innerIndex) => {
                                    const to = buildCollegeListPath(
                                      label,
                                      innerItem.label,
                                      innerItem.slug
                                    );
                                    return (
                                      <li className="inner-menu-item" key={innerIndex + 12}>
                                        {to ? (
                                          <Link
                                            to={to}
                                            onClick={() => {
                                              setIsSubMenuVisible(false);
                                              setOpenInnerIndex(null);
                                            }}
                                          >
                                            {innerItem.label}
                                          </Link>
                                        ) : (
                                          <span>{innerItem.label}</span>
                                        )}
                                      </li>
                                    );
                                  })}
                              </ul>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </li>
              );
            })}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const NavigationMenu = () => {
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    fetch(apiUrl + "/api/v1/menu", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "any",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setMenuItems(data.menu);
      })
      .catch((error) => {
        console.error("Error fetching menu data:", error);
      });
  }, []);
  return (
    <div className="navigation-menu">
      {menuItems.map((item, index) => (
        <MenuItem key={index} label={item.label} submenu={item.submenu} />
      ))}
    </div>
  );
};

export default NavigationMenu;

export const MobileNavigationMenu = ({ onNavigate }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [openTopIndex, setOpenTopIndex] = useState(null);
  const [openGroupByTop, setOpenGroupByTop] = useState({});

  useEffect(() => {
    fetch(apiUrl + "/api/v1/menu", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "any",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setMenuItems(data.menu);
      })
      .catch((error) => {
        console.error("Error fetching menu data:", error);
      });
  }, []);

  const toggleTop = (index) => {
    setOpenTopIndex((prev) => (prev === index ? null : index));
  };

  const toggleGroup = (topIndex, groupIndex) => {
    setOpenGroupByTop((prev) => {
      const current = prev[topIndex];
      return { ...prev, [topIndex]: current === groupIndex ? null : groupIndex };
    });
  };

  const rendered = useMemo(() => {
    return (Array.isArray(menuItems) ? menuItems : []).map((top, topIndex) => {
      const topOpen = openTopIndex === topIndex;
      const groups = Array.isArray(top?.submenu) ? top.submenu : [];
      const openGroupIndex = openGroupByTop[topIndex];

      const defaultLeaf = findDefaultLeaf(groups);
      const defaultTargetPath = defaultLeaf
        ? buildCollegeListPath(top?.label, defaultLeaf.label, defaultLeaf.slug)
        : null;

      return (
        <div key={topIndex} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <div className="w-full flex items-center justify-between">
            {defaultTargetPath ? (
              <Link
                to={defaultTargetPath}
                className="flex-1 px-4 py-3 text-sm font-semibold text-slate-900 no-underline"
                onClick={() => {
                  if (typeof onNavigate === "function") onNavigate();
                }}
              >
                {top?.label || "—"}
              </Link>
            ) : (
              <span className="flex-1 px-4 py-3 text-sm font-semibold text-slate-900">
                {top?.label || "—"}
              </span>
            )}
            <button
              type="button"
              onClick={() => toggleTop(topIndex)}
              aria-label={topOpen ? `Collapse ${top?.label || "menu"}` : `More ${top?.label || "menu"} options`}
              className="px-4 py-3 text-slate-500"
            >
              {topOpen ? "−" : "+"}
            </button>
          </div>

          {topOpen ? (
            <div className="border-t border-slate-200">
              {groups.map((group, groupIndex) => {
                const leaves = Array.isArray(group?.submenu) ? group.submenu : [];
                const groupOpen = openGroupIndex === groupIndex;
                return (
                  <div key={groupIndex} className="border-b border-slate-100 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => toggleGroup(topIndex, groupIndex)}
                      className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-800"
                    >
                      <span>{group?.label || "—"}</span>
                      <span className="text-slate-500">{groupOpen ? "−" : "+"}</span>
                    </button>

                    {groupOpen ? (
                      <div className="px-4 pb-3">
                        <div className="grid grid-cols-1 gap-2">
                          {leaves.map((leaf, leafIndex) => {
                            const to = buildCollegeListPath(top?.label, leaf?.label, leaf?.slug);
                            if (!to) return null;
                            return (
                              <Link
                                key={leafIndex}
                                to={to}
                                className="text-sm text-slate-700 hover:text-brand-700 no-underline"
                                onClick={() => {
                                  if (typeof onNavigate === "function") onNavigate();
                                }}
                              >
                                {leaf?.label}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      );
    });
  }, [menuItems, openTopIndex, openGroupByTop]);

  return <div className="space-y-3">{rendered}</div>;
};
